/**
 * How far back to sit when arriving at a system.
 *
 * "Jump to this system at cluster level" is a statement about detail, not about
 * distance — but the tracker only understands distance, so something has to
 * convert one into the other. That conversion lives here rather than in the
 * starmap component so it can be checked against the tracker directly: a
 * headless browser never runs `requestAnimationFrame`, so anything that only
 * happens inside the render loop cannot be tested at all.
 *
 * Both distances are derived from the tracker's own thresholds. Hard-coding
 * them would leave two sets of numbers to keep in agreement, and the failure
 * would be silent — you would simply arrive at the wrong zoom.
 */

import type { getGalaxyMap, Vec3 } from '@interspacial/shared';

import { CLUSTER_ENTER, SYSTEM_EXIT } from './lod.js';

export type FrameLevel = 'cluster' | 'system';

type GalaxyMap = ReturnType<typeof getGalaxyMap>;

/** Close enough to resolve a system's orbits, comfortably inside `SYSTEM_ENTER`. */
export const SYSTEM_FRAME_DISTANCE = 370;

/**
 * A view distance that lands on the cluster tier for a system in this cluster.
 *
 * Far enough out that the system stops resolving, near enough that its cluster
 * still does. Sized from the host cluster's radius, because that is what the
 * cluster threshold scales with — one fixed distance would sit inside a large
 * cluster and outside a small one.
 */
export function clusterFrameDistance(map: GalaxyMap, position: Vec3): number {
  let radius = 0;
  let nearest = Infinity;

  for (const { cluster } of map) {
    const distance = Math.hypot(
      cluster.position.x - position.x,
      cluster.position.y - position.y,
      cluster.position.z - position.z,
    );
    if (distance < nearest) {
      nearest = distance;
      radius = cluster.radius;
    }
  }

  // Kept below the cluster threshold with room to spare, so drifting slightly
  // does not drop straight back out to the galaxy view.
  return Math.max(SYSTEM_EXIT * 1.2, Math.min(radius * 1.7, radius * CLUSTER_ENTER * 0.9));
}

export function frameDistance(level: FrameLevel, map: GalaxyMap, position: Vec3): number {
  return level === 'system' ? SYSTEM_FRAME_DISTANCE : clusterFrameDistance(map, position);
}
